import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import { Statement } from '../../../types/finance';
import { Page, pdfColors, pdfSpacing, fmtDate, type SchoolBranding } from '../../../utils/pdf';
import StatementTable from './StatementTable';

interface Props {
  statement: Statement;
  branding: SchoolBranding;
  generatedAt: string;
}

const styles = StyleSheet.create({
  warning: { marginTop: pdfSpacing.md, fontSize: 8, color: pdfColors.danger },
});

/**
 * A financial statement — profit & loss, balance sheet or cash flow — as
 * the Statements screen shows it, under the letterhead: the period (or the
 * date) it covers, then the statement with a column per period compared.
 */
const StatementDocument: React.FC<Props> = ({ statement, branding, generatedAt }) => {
  // The first column is the period asked for — "1 Jul 2026 – 3 Nov 2026", or "As at 3 Nov 2026".
  const period = statement.columns[0]?.label ?? '';
  return (
    <Document title={`${statement.title} — ${period}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{ title: statement.title, subtitle: period, generatedAt }}
        official={{ details: [
          statement.kind === 'balance-sheet' ? { value: period } : { label: 'Period', value: period },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ] }}
      >
        <StatementTable title={statement.title} columns={statement.columns.map(c => c.label)} lines={statement.lines} />
        {statement.balanced === false && (
          <Text style={styles.warning}>Assets do not equal liabilities and equity — check the trial balance.</Text>
        )}
      </Page>
    </Document>
  );
};

export default StatementDocument;
